/**
 * Profile Tab Content Component (Feature 022)
 *
 * Main orchestrator for the Service Provider Profile tab.
 * Shows either:
 * - Profile preview with two sub-tabs (Preview Profile + Information) for approved providers
 * - Placeholder message for non-approved providers
 */

import { useEffect, useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ContactUsButton } from './ContactUsButton';
import { ProfileUpdateRequestModal } from './ProfileUpdateRequestModal';
import { AccountInformationCard } from './AccountInformationCard';
import { SPProviderDetailsWrapper } from './preview/SPProviderDetailsWrapper';
import { ReadOnlyInformationTab } from './information/ReadOnlyInformationTab';
import { SchedulingLinkCard } from './SchedulingLinkCard';
import { SpecialtyCoverageSection } from './SpecialtyCoverageSection';
import { ProviderDocumentsManager } from '@/features/provider-directory/components/ProviderDocumentsManager';
import { shouldShowProfilePreview } from '../utils/profilePreview';
import { useUpdateCalendlyUrl } from '../hooks/useUpdateCalendlyUrl';
import { useToast } from '@/shared/components/Toast';
import { PermissionGate } from '@/features/workspace-sub-delegation';
import type { SpecialtyOption } from '@/shared/components/specialty-coverage';
import type { ServiceProviderProfile } from '../api/profileApi';

type ProfileTabContentProps = {
  profile: ServiceProviderProfile | undefined;
  isOnboardingComplete: boolean;
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  vettingStatus: 'PENDING' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED';
  /** Bubbles the profile form's dirty state up so the page can guard top-level tab switches. */
  onDirtyChange?: (dirty: boolean) => void;
};

/** Prompt shown before discarding unsaved profile edits on any tab switch. */
export const UNSAVED_CHANGES_PROMPT =
  'You have unsaved profile changes. Leave without saving?';

export function ProfileTabContent({
  profile,
  isOnboardingComplete,
  accountStatus,
  vettingStatus,
  onDirtyChange,
}: ProfileTabContentProps) {
  const [activeSubTab, setActiveSubTab] = useState('preview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInfoFormDirty, setIsInfoFormDirty] = useState(false);
  const [isCoverageDirty, setIsCoverageDirty] = useState(false);
  const isFormDirty = isInfoFormDirty || isCoverageDirty;
  const updateCalendlyUrlMutation = useUpdateCalendlyUrl();
  const { showToast } = useToast();

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  // ponytail: native confirm() as the discard guard; upgrade path is the shared AlertDialog.
  const handleSubTabChange = (value: string) => {
    if (activeSubTab === 'information' && isFormDirty && !window.confirm(UNSAVED_CHANGES_PROMPT)) {
      return;
    }
    setActiveSubTab(value);
  };

  // Handle contact button click - opens modal
  const handleContactClick = () => {
    setIsModalOpen(true);
  };

  // Handle Calendly URL save (Feature 035)
  const handleSaveCalendlyUrl = async (url: string): Promise<void> => {
    try {
      await updateCalendlyUrlMutation.mutateAsync(url);
      showToast('Scheduling link updated successfully!', 'success');
    } catch (error) {
      showToast('Failed to update scheduling link. Please try again.', 'error');
      throw error; // Re-throw so the card can handle it
    }
  };

  // Check if we should show profile preview
  const showPreview = profile && shouldShowProfilePreview(
    profile.user?.accountStatus,
    profile.vettingStatus
  );

  // Show placeholder if profile preview is not available
  if (!showPreview) {
    // Show specific message for suspended accounts
    const isSuspended = accountStatus === 'SUSPENDED';

    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl text-text-primary">Your Profile Preview</h2>
          {!isOnboardingComplete && !isSuspended && (
            <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
              Draft - Not Live
            </Badge>
          )}
        </div>
        <div className="space-y-4 p-6 bg-card-subtle rounded-lg">
          <p className="text-text-secondary">
            {isSuspended ? (
              <>
                Account suspended. Reach out to{' '}
                <a href="mailto:info@thebquest.com" className="text-sage hover:underline">
                  info@thebquest.com
                </a>{' '}
                for more information.
              </>
            ) : (
              'Profile preview is currently unavailable.'
            )}
          </p>
        </div>
      </Card>
    );
  }

  // Show full preview with sub-tabs
  return (
    <div className="space-y-4">
      {/* Draft/Not Live Banner - shown when profile is not yet public */}
      {!isOnboardingComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
              Draft - Not Live
            </Badge>
            <p className="text-sm text-yellow-800">
              Your profile is not yet visible to other users. Complete your onboarding to make it live.
            </p>
          </div>
        </div>
      )}

      {/* Account Information Card and Contact Button - same width */}
      <div className="inline-block">
        <div className="space-y-3">
          <AccountInformationCard
            accountStatus={accountStatus}
            vettingStatus={vettingStatus}
          />
          <ContactUsButton onContactClick={handleContactClick} />
        </div>
      </div>

      {/* Profile Update Request Modal */}
      <ProfileUpdateRequestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        providerName={profile.companyName || 'Unknown Provider'}
        providerId={profile.id}
        providerEmail={profile.user?.email || ''}
      />

      {/* Sub-Tabs */}
      <Tabs value={activeSubTab} onValueChange={handleSubTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="preview">Preview Profile</TabsTrigger>
          <TabsTrigger value="information">Information</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="mt-6">
          <SPProviderDetailsWrapper profile={profile} />
        </TabsContent>

        <TabsContent value="information" className="mt-6">
          {/* Editing the owner's profile requires PROFILE_MANAGEMENT; the
              read-only Preview sub-tab above stays visible to any delegate. */}
          <PermissionGate permission="PROFILE_MANAGEMENT" feature="profile management">
            <div className="space-y-4">
              {/* Scheduling Link Card (Feature 035) - Top item in Information tab */}
              <SchedulingLinkCard
                calendlyUrl={profile.calendlyUrl}
                onSave={handleSaveCalendlyUrl}
                isLoading={updateCalendlyUrlMutation.isPending}
              />

              {/* Per-specialty coverage (Feature 090) — SP edits coverage/ranges only */}
              <SpecialtyCoverageSection
                specialtyOptions={(profile.specialties ?? []).map(
                  (spec): SpecialtyOption => ({
                    id: spec.specialty?.id ?? spec.specialtyId,
                    name: spec.specialty?.name ?? spec.specialtyId,
                  }),
                )}
                onDirtyChange={setIsCoverageDirty}
              />

              {/* Read-only information sections */}
              <ReadOnlyInformationTab profile={profile} onDirtyChange={setIsInfoFormDirty} />
            </div>
          </PermissionGate>
        </TabsContent>

        {/* Feature 073: PDF documents — the only writable surface in this
            otherwise read-only SP self-edit profile view. */}
        <TabsContent value="documents" className="mt-6">
          <ProviderDocumentsManager mode="sp-self" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
